<template>
  <VdCard class="devices-list-item-component" size="small" data-cy="testers-list">
    <div class="device-card-content">
      <section class="left">
        <vd-custom-icon name="phone-icon" big />
        <h3 data-cy="tester">{{ device.name }}</h3>
        &nbsp;
        <vd-button
          v-if="!!assignations?.length"
          size="small"
          icon-left="fa:trash-can"
          @click="() => onRemoveAssignations({ deviceId: device.id })"
        >
          Remove assignations
        </vd-button>
      </section>
      <section class="right">
        <div class="device-id">{{ device.id }}</div>
        <div class="divider"></div>
        <vd-dropdown v-if="canWriteTesters">
          <vd-dropdown-item dangerous :disabled="!!device.userId" @click="onDelete">
            {{ !!device.userId ? "You cannot delete this device" : "Delete" }}
          </vd-dropdown-item>
        </vd-dropdown>
      </section>
    </div>
    <div v-if="assignations && assignations.length">
      <p>Currently testing the following A/B Tests:</p>
      <div>
        <div
          v-for="{ testDevice, abtestId, cohortId, abtestName, cohortName, layer } in assignations"
          :key="`${testDevice.id}-${cohortName}`"
          class="testing-item"
        >
          <span>Layer - {{ layer?.name }}</span>
          <VdIcon store="fa" name="chevron-right" size="small" />
          <span>{{ abtestName ?? `AB Test ${abtestId}` }}</span>
          <VdIcon store="fa" name="chevron-right" size="small" />
          <strong>{{ cohortName ?? `Cohort ${cohortId}` }}</strong>
        </div>
      </div>
    </div>
    <div v-else>
      <p>No active tests.</p>
    </div>
    <span v-if="!!device.userId" class="created-by">Created by {{ device.userId }}</span>
  </VdCard>
</template>

<script lang="ts">
import { VdCard } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { computed, defineComponent, PropType } from "vue";

import { pluralize } from "@/filters";
import { TestDevice, TestDeviceAssignationsListPageTestDeviceAssignationFragment } from "@/graphql";
import usePermissions from "@/hooks/use-permissions";

export default defineComponent({
  name: "DevicesListItem",

  components: {
    VdIcon,
    VdCard,
  },

  props: {
    device: {
      type: Object as PropType<TestDevice>,
      required: true,
    },

    assignations: {
      type: Array as PropType<TestDeviceAssignationsListPageTestDeviceAssignationFragment[]>,
      required: false,
      default: () => [],
    },
  },

  emits: ["remove-assignations", "delete"],

  setup(props, { emit }) {
    const { canWriteTesters } = usePermissions();

    /**
     * Only get the assignations linked to one device
     */
    const nbAssignations = computed(() => props.assignations?.length ?? 0);

    const onRemoveAssignations = ({ deviceId }: { deviceId: string }) => {
      emit("remove-assignations", { deviceId });
    };

    const onDelete = () => {
      emit("delete", props.device.id);
    };

    return {
      onRemoveAssignations,
      nbAssignations,
      canWriteTesters,
      onDelete,
      pluralize,
    };
  },
});
</script>

<style lang="scss">
.devices-list-item-component {
  height: 100%;
  margin-bottom: size("ML");

  .device-card-content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .left {
    align-self: stretch;
    display: flex;
    align-items: center;
  }

  .right {
    align-self: stretch;
    display: flex;
    justify-content: space-evenly;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    .device-id {
      font-size: font("M");
      color: $grey;
    }

    .divider {
      width: 0;
      height: 100%;
      border: 1px solid $foggy-grey;
      margin: 0 20px;
    }

    .devices-number {
      display: flex;
      align-items: center;
      font-size: font("M");
      color: $slate;
      margin-right: 50px;
    }
  }

  .testing-item {
    display: flex;
    align-items: center;
    column-gap: size("S");
    margin-bottom: size("XS");
  }

  .created-by {
    color: var(--vd-color-grey-light-5);
    font-size: font("S");
  }
}
</style>
